import { Component, h, Element, State, Listen } from "@stencil/core";

import {getShortMonth} from "../../utils/date";
import { i18n } from '../../utils/I18n';
import {getOfferName, getTrainCategoryForName, translateCompartmentName} from "../../utils/offers";
import {DATA_STORE_KEY_PASSENGERS, DATA_STORE_KEY_PREBOOK, dataStore} from '../../global/DataStore'
import {FinalProductData, journeyData, JourneyStoreEvent, NjOfferData} from "../../global/JourneyStore";
import { formatPrice } from "../../helpers/utils";
import {SelectedUser} from "../nj-passangers/nj-passangers";
import { PassangerType } from "../nj-passangers/PassangerType";
import { removeJourney } from '../nj-reservation/nj-reservation-utils';
import { ReservationMode } from "../nj-reservation/ReservationMode";

@Component({
  tag: "nj-shopping-cart",
  styleUrl: "nj-shopping-cart.scss",
  shadow: true
})
export class NjShoppingCart {

    shoppingCartPrice: number = 0.00
    prebookData: any = null
	private insurancePremium = 0;

    @Element() el: HTMLElement;

    @State() outwardOffer: NjOfferData | null = null
    @State() returnOffer: NjOfferData | null = null
    @State() passangers: SelectedUser[] = null

    @State() selectedOutwardProduct: FinalProductData | null = null;
    @State() selectedReturnProduct: FinalProductData | null = null;

    componentWillLoad() {
        this.init();
    }

    @Listen('productSelected',{ target: 'window' })
    productSelectedHandler(event: JourneyStoreEvent) {
        // TODO Do we really need this? Product selection triggers prebook preparation, for which we listen below anyway
        if (!event.detail) {
            this.selectedOutwardProduct = null;
            this.selectedReturnProduct = null;
            return;
        }

        const product = journeyData(event.detail.reservationMode).getFinalProduct();
        if (event.detail.reservationMode == ReservationMode.Outward) {
            this.selectedOutwardProduct = product;
        } else if (event.detail.reservationMode == ReservationMode.Return) {
            this.selectedReturnProduct = product;
        }
    }

    @Listen('prebookRequestPrepared', { target: 'window' })
    onPrebookRequestPrepared() {
        this.init()
    }

    init() {
        const outwardData = journeyData(ReservationMode.Outward);
        const returnData = journeyData(ReservationMode.Return);

        this.outwardOffer = outwardData.getFinalOffer();
        this.returnOffer = returnData.getFinalOffer();
        this.passangers = dataStore().getSessionData(DATA_STORE_KEY_PASSENGERS, null)

        this.prebookData = dataStore().getSessionData(DATA_STORE_KEY_PREBOOK, this.prebookData)
        this.shoppingCartPrice = (this.prebookData ? this.prebookData.paymentInformation.amount : this.shoppingCartPrice)
		this.insurancePremium = (this.prebookData?.insuranceSelected && this.prebookData.insurancePremium) ?? 0;

        this.selectedOutwardProduct = outwardData.getFinalProduct();
        this.selectedReturnProduct = returnData.getFinalProduct();
    }

    handleRemoveJourney(reservationMode: ReservationMode) {
        switch (reservationMode) {
            case ReservationMode.Outward:
                this.outwardOffer = null;
                break;
            case ReservationMode.Return:
                this.returnOffer = null;
                break;
        }

        removeJourney(reservationMode);
    }

    renderConnectionData(connectionData: NjOfferData | null, reservationMode: ReservationMode) {
        if (connectionData) {
            let connection = connectionData.connection
            let depDate = connection.departure;
            const trainType = getTrainCategoryForName(connection.train);

            return(
                <div class={{
                    'train-color-default': true,
                    [`train-color-${trainType?.toLowerCase()}`]: !!trainType,
                }}>
                    <h3 class="shopping-cart__detail-title">{i18n.getMessage(`cart.header.${reservationMode.toLowerCase()}`)}</h3>
                    {this.outwardOffer && this.returnOffer ?
                        <button class="shopping-cart__detail-remove" onClick={() => this.handleRemoveJourney(reservationMode)}>
                            <delete-icon/>
                            <span class="screen-reader-text">{i18n.getMessage(`cart.remove.${reservationMode.toLowerCase()}`)}</span>
                        </button>
                        : null}
                    <div class="flex">
                        <div class="calendar">
                            <span class="calendar__year">{depDate.format('YYYY')}</span>
                            <div class="calendar__body">
                                <span class="calendar__day">{depDate.format('D')}</span>
                                <span class="calendar__month">{getShortMonth(depDate)}</span>
                            </div>
                        </div>
                        <span class="shopping-cart__detail-highlight">
                            {connection.from.name} <arrow-right-icon></arrow-right-icon> {connection.to.name}
                        </span>
                    </div>
                    <div class="flex shopping-cart__detail-connection">
                        <span class="shopping-cart__detail-highlight">{connection.from.dep_time}</span>
                        <span class="shopping-cart__detail-train">
                            <nj-train-icon name={connection.train} category={trainType} hideIcon={true}/>
                        </span>
                        <span class="shopping-cart__detail-highlight">{connection.to.arr_time}</span>
                    </div>
                </div>
            )
        }
    }

    renderPassangerData() {
        return (
            <div class="shopping-cart__detail">
                <div class="shopping-cart__detail-header">

                    <h3 class="shopping-cart__detail-title">{i18n.getMessage('cart.passengers.header')}</h3>
                </div>

                <ul class="shopping-cart__detail-list">
                    {this.renderPassangerDataByType(PassangerType.Adult)}
					{this.renderPassangerDataByType(PassangerType.TenToFourteenYearOldChild)}
					{this.renderPassangerDataByType(PassangerType.Child)}
                    {this.renderPassangerDataByType(PassangerType.SmallChildSeat)}
                    {this.renderPassangerDataByType(PassangerType.SmallChild)}
                    {this.renderPassangerDataByType(PassangerType.Bike)}
                    {/* {this.renderPassangerDataChallenedOptions()} */}
                </ul>
            </div>
        )
    }

    renderPassangerDataByType(passangerType: PassangerType) {
        return this.passangers
            .filter(passanger => passanger.njPassangerType === passangerType)
            .map((passanger, i) => [
                <li>
                    <span class="shopping-cart__detail-highlight">
                        {i + 1}.&nbsp;{i18n.getMessage(`passengers.${passangerType}`)}
                    </span>
                    <span>
                        {passanger.njCards.map(card =>
                            i18n.getMessage(`ts.reductionCard.name.${card.code}`, '', null, card.cardName)
                        ).join(", ")}
                    </span>
                </li>,
                passanger.njChallengedOptions?.wheelchair ? (
                    <li>
                        <span class="shopping-cart__detail-highlight shopping-cart__detail-highlight--indented">
                            {i18n.getMessage('passengers.wheelchair')}
                        </span>
                    </li>
                ) : null,
                passanger.njChallengedOptions?.attendant ? (
                    <li>
                        <span class="shopping-cart__detail-highlight shopping-cart__detail-highlight--indented">
                            {i18n.getMessage('passengers.attendant')}
                        </span>
                    </li>
                ) : null,
                passanger.njChallengedOptions?.dog ? (
                    <li>
                        <span class="shopping-cart__detail-highlight shopping-cart__detail-highlight--indented">
                            {i18n.getMessage('passengers.dog')}
                        </span>
                    </li>
                ) : null,
            ]);
    }

    renderSelectedProduct(selectedProduct: FinalProductData) {
        return (
            <div>
                <div class="shopping-cart__product">
                    <div>
                        { translateCompartmentName(selectedProduct.njProduct.compartment) }
                    </div>
                    <div class="shopping-cart__product_ticket">
                        {i18n.getMessage(`ticket-type.${selectedProduct.njProduct.productType}`)} {i18n.getMessage('cart.withreservation')}
                    </div>
                    <div class="shopping-cart__product_price">
                        {formatPrice(selectedProduct.njProduct.price)} EUR
                    </div>
                </div>
                {selectedProduct.additionalProduct ? (
                    <div class="shopping-cart__product">
                        <div>
                            {i18n.getMessage('cart.additional.label')}
                        </div>
                        <div class="shopping-cart__product_ticket">
                            {getOfferName(selectedProduct.additionalProduct.offer, selectedProduct.additionalProduct.njIndex)}{' '}
                            {i18n.getMessage(`cart.class.${selectedProduct.additionalProduct.priceClass}`)}{' '}
                            {i18n.getMessage('cart.withreservation')}
                        </div>
                        <div class="shopping-cart__product_price">
                            {formatPrice(selectedProduct.additionalProduct.price - selectedProduct.njProduct.price)} EUR
                        </div>
                    </div>
                ) : null}
            </div>
        )
    }

	private renderInsurance() {
		return this.insurancePremium ? (
			<div class="shopping-cart__detail shopping-cart__insurance">
				<h3 class="shopping-cart__detail-title">{i18n.getMessage(`cart.insurance.header`)}</h3>
				<div class="shopping-cart__product">
					<div class="shopping-cart__product_price">
						{formatPrice(this.insurancePremium)} EUR
					</div>
				</div>
			</div>
		) : null;
	}

    render()  {
        if (!this.outwardOffer && !this.returnOffer) {
            return (
                <div role="region" aria-label="Einkaufswagen" aria-live="polite" class={"shopping-cart"}>
                    <div class="shopping-cart__content">
                        <h2>
                            <tickets-icon class="icon" width={20} height={20}></tickets-icon>&nbsp;
                            {i18n.getMessage('cart.header')}
                        </h2>
                        <p>{i18n.getMessage('cart.noselection')}</p>
                    </div>
                </div>
            )
        }

        return (
            <div role="region" aria-labelledby="shopping-cart-title" aria-live="polite" class={"shopping-cart"}>
                <div class="shopping-cart__content">
                    <h2 id="shopping-cart-title">
                        <tickets-icon aria-hidden="true" class="icon" width={20} height={20}></tickets-icon>&nbsp;
                        {i18n.getMessage('cart.header')}
                    </h2>
                    <div class="shopping-cart__detail">
                        {this.outwardOffer ? this.renderConnectionData(this.outwardOffer, ReservationMode.Outward) : null}
                        {this.selectedOutwardProduct ? this.renderSelectedProduct(this.selectedOutwardProduct) : null}
                    </div>
                    {this.returnOffer ?
                        <div class="shopping-cart__detail">
                            {this.renderConnectionData(this.returnOffer, ReservationMode.Return)}
                            {this.selectedReturnProduct ? this.renderSelectedProduct(this.selectedReturnProduct) : null}
                        </div>
                        : null}
					{this.renderInsurance()}
                    {(this.passangers) ? this.renderPassangerData() : ""}
                </div>
                <div class="shopping-cart__price">
                    <span>{i18n.getMessage('cart.total')}</span>
                    <span>{formatPrice(this.shoppingCartPrice + this.insurancePremium)}&nbsp;€</span>
                </div>
            </div>
        )
    }

}
